import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Plus, Minus, User, Mail, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import DancerPaymentShareManager from '../DancerPaymentShareManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EditWeekenderBookingDialog - Extracted from AdminWeekendsTab.js
 * Provides full booking edit capability including:
 * - Customer/contact details
 * - Attendee/guests management
 * - Room selection with auto-guest adjustment
 * - Add-ons selection
 * - Special requests
 * - Status management
 * - Financial overrides (deposit, total, amount_paid)
 * - Recalculate totals functionality
 * - DancerPaymentShareManager integration
 */
export default function EditWeekenderBookingDialog({
  booking,
  event,
  open,
  onClose,
  authToken,
  onSuccess
}) {
  const [saving, setSaving] = useState(false);
  const [loadedEvent, setLoadedEvent] = useState(event);
  const [calculatedTotals, setCalculatedTotals] = useState(null);
  
  // Edit form state - mirrors legacy implementation
  const [editBookingForm, setEditBookingForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    guests: [],
    selected_add_ons: [],
    addOnQuantities: {},
    special_requests: '',
    status: '',
    rooms: {},
    deposit_override: null,
    total_override: null,
    amount_paid: 0,
    discountType: 'none',
    discountValue: '',
    discountReason: ''
  });

  // Initialize form when dialog opens with booking data
  useEffect(() => {
    const initializeForm = async () => {
      if (!booking || !open) return;

      // Fetch event if not provided or mismatched
      let eventData = event;
      if (!eventData || eventData.event_id !== booking.event_id) {
        try {
          const response = await fetch(`${API_URL}/api/weekends/events/${booking.event_id}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (response.ok) {
            eventData = await response.json();
            setLoadedEvent(eventData);
          }
        } catch (error) {
          console.error('Error fetching event:', error);
        }
      } else {
        setLoadedEvent(eventData);
      }

      // Convert rooms array to object format for editing
      const roomsObj = {};
      (booking.rooms || []).forEach(room => {
        const roomType = room.room_type || room.type;
        roomsObj[roomType] = room.quantity;
      });

      setEditBookingForm({
        customer_name: booking.customer_name || '',
        customer_email: booking.customer_email || '',
        customer_phone: booking.customer_phone || '',
        guests: (booking.guests || []).map(g => ({
          first_name: g.first_name || '',
          last_name: g.last_name || '',
          email: g.email || ''
        })),
        selected_add_ons: (booking.selected_add_ons || []).map(a => typeof a === 'string' ? a : a.name),
        addOnQuantities: (booking.selected_add_ons || []).reduce((acc, a) => {
          if (a && typeof a === 'object' && a.name) {
            const q = a.quantity != null ? a.quantity : (a.guests != null ? a.guests : 0);
            if (q > 0) acc[a.name] = q;
          } else if (typeof a === 'string') {
            // Legacy string entry => treat as all-guests
            acc[a] = booking.number_of_guests || 0;
          }
          return acc;
        }, {}),
        special_requests: booking.special_requests || '',
        status: booking.status || 'pending',
        rooms: roomsObj,
        deposit_override: booking.deposit_override || null,
        total_override: booking.total_override || null,
        amount_paid: booking.amount_paid || 0,
        discountType: booking.discount?.type || 'none',
        discountValue: booking.discount?.value != null ? String(booking.discount.value) : '',
        discountReason: booking.discount?.reason || ''
      });
      setCalculatedTotals(null);
    };

    initializeForm();
  }, [booking, event, open, authToken]);

  // Room quantity adjustment with auto guest count
  const updateEditRoomQuantity = (roomType, delta) => {
    setEditBookingForm(prev => {
      const currentQty = prev.rooms[roomType] || 0;
      const newQty = Math.max(0, currentQty + delta);
      const newRooms = { ...prev.rooms };

      if (newQty === 0) {
        delete newRooms[roomType];
      } else {
        newRooms[roomType] = newQty;
      }

      // Calculate total guests based on room capacities
      const roomOptions = loadedEvent?.room_options || loadedEvent?.room_types || [];
      let totalGuests = 0;
      Object.entries(newRooms).forEach(([type, qty]) => {
        const roomDef = roomOptions.find(r => r.type.toLowerCase() === type.toLowerCase());
        if (roomDef) {
          totalGuests += (roomDef.capacity || 2) * qty;
        }
      });

      // Adjust guests array
      const newGuests = [...prev.guests];
      while (newGuests.length < totalGuests) {
        newGuests.push({ first_name: '', last_name: '', email: '' });
      }
      while (newGuests.length > totalGuests) {
        newGuests.pop();
      }

      // Clamp any add-on quantity that now exceeds the reduced guest count
      const clampedAddOns = {};
      Object.entries(prev.addOnQuantities || {}).forEach(([name, qty]) => {
        const c = Math.min(qty, totalGuests);
        if (c > 0) clampedAddOns[name] = c;
      });

      return { ...prev, rooms: newRooms, guests: newGuests, addOnQuantities: clampedAddOns };
    });
  };

  // Add-on passenger quantity setter (clamped 0..totalGuests)
  const setEditAddOnQuantity = (addOnName, qty) => {
    setEditBookingForm(prev => {
      const roomOptions = loadedEvent?.room_options || loadedEvent?.room_types || [];
      let totalGuests = 0;
      Object.entries(prev.rooms).forEach(([type, q]) => {
        const roomDef = roomOptions.find(r => r.type.toLowerCase() === type.toLowerCase());
        if (roomDef) totalGuests += (roomDef.capacity || 2) * q;
      });
      const clamped = Math.max(0, Math.min(qty, totalGuests));
      const next = { ...prev.addOnQuantities };
      if (clamped <= 0) delete next[addOnName];
      else next[addOnName] = clamped;
      return { ...prev, addOnQuantities: next };
    });
  };

  // Update guest name/email
  const updateEditGuestName = (index, field, value) => {
    setEditBookingForm(prev => {
      const newGuests = [...prev.guests];
      if (!newGuests[index]) {
        newGuests[index] = { first_name: '', last_name: '', email: '' };
      }
      newGuests[index][field] = value;
      return { ...prev, guests: newGuests };
    });
  };

  // Compute totals (pure) from the current form state
  const computeTotals = () => {
    if (!loadedEvent || !editBookingForm.rooms) return null;

    const roomOptions = loadedEvent.room_options || loadedEvent.room_types || [];
    let roomsTotal = 0;
    let roomDeposit = 0;
    let totalGuests = 0;

    Object.entries(editBookingForm.rooms).forEach(([type, quantity]) => {
      if (quantity > 0) {
        const roomDef = roomOptions.find(r => r.type.toLowerCase() === type.toLowerCase());
        if (roomDef) {
          const capacity = roomDef.capacity || 2;
          const pricePerPerson = roomDef.price_per_person || 0;
          const depositPerPerson = roomDef.deposit_per_person || 50;
          const guestsInRoom = capacity * quantity;

          roomsTotal += pricePerPerson * guestsInRoom;
          roomDeposit += depositPerPerson * guestsInRoom;
          totalGuests += guestsInRoom;
        }
      }
    });

    // Calculate add-ons total live from selected passenger quantities
    const eventAddOns = loadedEvent.add_ons || [];
    let addOnsTotal = 0;
    let addOnsDeposit = 0;
    Object.entries(editBookingForm.addOnQuantities || {}).forEach(([name, qty]) => {
      const addOn = eventAddOns.find(a => a.name === name);
      if (addOn && qty > 0) {
        addOnsTotal += (addOn.price || 0) * qty;
        addOnsDeposit += (addOn.deposit_amount || 0) * qty;
      }
    });

    const subtotal = roomsTotal + addOnsTotal;
    const totalDeposit = roomDeposit + addOnsDeposit;
    const amountPaid = booking?.amount_paid || 0;

    // Discount preview from the current form state (applies to rooms subtotal only)
    let discountAmount = 0;
    if (editBookingForm.discountType && editBookingForm.discountType !== 'none') {
      const val = parseFloat(editBookingForm.discountValue) || 0;
      if (val > 0) {
        discountAmount = editBookingForm.discountType === 'percentage'
          ? (roomsTotal * val) / 100
          : val;
        discountAmount = Math.min(discountAmount, roomsTotal);
        discountAmount = Math.max(0, Math.round(discountAmount * 100) / 100);
      }
    }
    const discountedSubtotal = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100);
    const balance = discountedSubtotal - amountPaid;

    return {
      roomsTotal,
      roomDeposit,
      addOnsTotal,
      addOnsDeposit,
      subtotal,
      discountAmount,
      discountedSubtotal,
      totalDeposit: Math.min(totalDeposit, discountedSubtotal),
      totalGuests,
      amountPaid,
      balance
    };
  };

  // Recalculate totals for display (button-triggered, with feedback)
  const recalculateTotals = () => {
    const t = computeTotals();
    if (!t) return;
    setCalculatedTotals(t);
    toast.success('Totals recalculated based on current room selection');
  };

  // Keep the totals panel in sync live as rooms/add-ons change
  useEffect(() => {
    const t = computeTotals();
    if (t) setCalculatedTotals(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editBookingForm.rooms, editBookingForm.addOnQuantities, editBookingForm.discountType, editBookingForm.discountValue, loadedEvent]);

  // Apply calculated totals as overrides
  const applyCalculatedTotals = () => {
    if (!calculatedTotals) return;

    setEditBookingForm(prev => ({
      ...prev,
      deposit_override: calculatedTotals.totalDeposit.toFixed(2),
      total_override: calculatedTotals.discountedSubtotal.toFixed(2)
    }));

    toast.success('Calculated totals applied to override fields');
  };

  // Handle form submission
  const handleUpdateBooking = async () => {
    if (!booking) return;

    setSaving(true);
    try {
      // Build update payload with only changed fields
      const updatePayload = {};

      if (editBookingForm.customer_name !== booking.customer_name) {
        updatePayload.customer_name = editBookingForm.customer_name;
      }
      if (editBookingForm.customer_email !== booking.customer_email) {
        updatePayload.customer_email = editBookingForm.customer_email;
      }
      if (editBookingForm.customer_phone !== booking.customer_phone) {
        updatePayload.customer_phone = editBookingForm.customer_phone;
      }
      if (editBookingForm.special_requests !== booking.special_requests) {
        updatePayload.special_requests = editBookingForm.special_requests;
      }
      if (editBookingForm.status !== booking.status) {
        updatePayload.status = editBookingForm.status;
      }

      // Always send guests if they exist (to ensure emails are saved)
      if (editBookingForm.guests && editBookingForm.guests.length > 0) {
        updatePayload.guests = editBookingForm.guests.map(g => ({
          first_name: g.first_name,
          last_name: g.last_name,
          email: g.email && g.email.trim() ? g.email.trim() : null
        }));
      }

      // Check if rooms have changed
      const currentRooms = {};
      (booking.rooms || []).forEach(r => {
        const roomType = r.room_type || r.type;
        currentRooms[roomType] = r.quantity;
      });

      const editRooms = editBookingForm.rooms || {};
      const allRoomTypes = new Set([...Object.keys(currentRooms), ...Object.keys(editRooms)]);
      let roomsChanged = false;
      for (const type of allRoomTypes) {
        if ((currentRooms[type] || 0) !== (editRooms[type] || 0)) {
          roomsChanged = true;
          break;
        }
      }

      if (roomsChanged) {
        const roomsArray = Object.entries(editBookingForm.rooms)
          .filter(([_, quantity]) => quantity > 0)
          .map(([type, quantity]) => {
            const roomDef = (loadedEvent?.room_options || loadedEvent?.room_types || [])
              .find(r => r.type.toLowerCase() === type.toLowerCase());
            return {
              type,
              quantity,
              price_per_person: roomDef?.price_per_person || 0,
              capacity: roomDef?.capacity || 2,
              deposit_per_person: roomDef?.deposit_per_person || 50
            };
          });
        updatePayload.rooms = roomsArray;
        // If rooms changed, also need to send updated guests
        updatePayload.guests = editBookingForm.guests.map(g => ({
          first_name: g.first_name,
          last_name: g.last_name,
          email: g.email && g.email.trim() ? g.email.trim() : null
        }));
      }

      // Handle overrides
      if (editBookingForm.deposit_override) {
        updatePayload.deposit_override = parseFloat(editBookingForm.deposit_override);
      }
      if (editBookingForm.total_override) {
        updatePayload.total_override = parseFloat(editBookingForm.total_override);
      }

      // Handle amount_paid correction
      const currentAmountPaid = booking.amount_paid || 0;
      const newAmountPaid = parseFloat(editBookingForm.amount_paid) || 0;
      if (newAmountPaid !== currentAmountPaid) {
        updatePayload.amount_paid = newAmountPaid;
      }

      // Check if add-ons changed (compare passenger quantities against stored booking)
      const currentQtyMap = {};
      (booking.selected_add_ons || []).forEach(a => {
        if (a && typeof a === 'object' && a.name) {
          const q = a.quantity != null ? a.quantity : (a.guests != null ? a.guests : 0);
          if (q > 0) currentQtyMap[a.name] = q;
        } else if (typeof a === 'string') {
          currentQtyMap[a] = booking.number_of_guests || 0;
        }
      });
      const newQtyMap = {};
      Object.entries(editBookingForm.addOnQuantities || {}).forEach(([name, qty]) => {
        if (qty > 0) newQtyMap[name] = qty;
      });
      if (JSON.stringify(newQtyMap) !== JSON.stringify(currentQtyMap)) {
        updatePayload.add_on_selections = Object.entries(newQtyMap).map(([name, quantity]) => ({ name, quantity }));
      }

      // Discount: apply/change/remove
      const hadDiscount = !!booking.discount;
      const nowType = editBookingForm.discountType;
      if (nowType !== 'none') {
        const valNum = parseFloat(editBookingForm.discountValue) || 0;
        if (!valNum || valNum <= 0) {
          toast.error('Enter a discount value greater than 0');
          setSaving(false);
          return;
        }
        if (nowType === 'percentage' && valNum > 100) {
          toast.error('Percentage discount cannot exceed 100%');
          setSaving(false);
          return;
        }
        const changed = !hadDiscount
          || booking.discount.type !== nowType
          || Number(booking.discount.value) !== valNum
          || (booking.discount.reason || '') !== (editBookingForm.discountReason || '').trim();
        if (changed) {
          if (!editBookingForm.discountReason?.trim()) {
            toast.error('A reason is required for the discount');
            setSaving(false);
            return;
          }
          updatePayload.discount_type = nowType;
          updatePayload.discount_value = valNum;
          updatePayload.discount_reason = editBookingForm.discountReason.trim();
        }
      } else if (hadDiscount) {
        updatePayload.remove_discount = true;
      }

      if (Object.keys(updatePayload).length === 0) {
        toast.info('No changes to save');
        onClose();
        return;
      }

      const response = await fetch(`${API_URL}/api/weekends/admin/bookings/${booking.booking_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        toast.success('Booking updated successfully');
        onClose();
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        console.error('Update error:', error);
        toast.error(error.detail || 'Failed to update booking');
      }
    } catch (error) {
      console.error('Update exception:', error);
      toast.error('Failed to update booking: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;

  const roomOptions = loadedEvent?.room_options || loadedEvent?.room_types || [];
  const addOns = loadedEvent?.add_ons || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Edit Booking</DialogTitle>
          <DialogDescription className="text-slate-600">
            Update booking details for {booking?.customer_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Customer Details */}
          <div className="space-y-3">
            <h3 className="font-medium text-sm text-slate-700">Customer Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-slate-700">Full Name</Label>
                <Input
                  value={editBookingForm.customer_name}
                  onChange={(e) => setEditBookingForm(prev => ({ ...prev, customer_name: e.target.value }))}
                  className="bg-white border-slate-300"
                  data-testid="edit-booking-customer-name"
                />
              </div>
              <div>
                <Label className="text-slate-700">Email</Label>
                <Input
                  type="email"
                  value={editBookingForm.customer_email}
                  onChange={(e) => setEditBookingForm(prev => ({ ...prev, customer_email: e.target.value }))}
                  className="bg-white border-slate-300"
                  data-testid="edit-booking-customer-email"
                />
              </div>
              <div>
                <Label className="text-slate-700">Phone</Label>
                <Input
                  value={editBookingForm.customer_phone}
                  onChange={(e) => setEditBookingForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                  className="bg-white border-slate-300"
                  data-testid="edit-booking-customer-phone"
                />
              </div>
            </div>
          </div>

          {/* Room Selection */}
          {roomOptions.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-slate-700">Rooms</h3>
              <div className="space-y-2">
                {roomOptions.map(room => (
                  <div key={room.type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium text-slate-900">{room.type}</span>
                      <span className="text-slate-500 text-sm ml-2">
                        (£{room.price_per_person}/person{room.capacity ? `, ${room.capacity} guests` : ''})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => updateEditRoomQuantity(room.type, -1)}
                        disabled={(editBookingForm.rooms[room.type] || 0) === 0}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-room-decrease-${room.type}`}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-medium text-slate-900">
                        {editBookingForm.rooms[room.type] || 0}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => updateEditRoomQuantity(room.type, 1)}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-room-increase-${room.type}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recalculate & Price Overrides */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-sm text-slate-700">Pricing</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={recalculateTotals}
                className="text-violet-600 border-violet-300 hover:bg-violet-50"
                data-testid="edit-booking-recalculate"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Recalculate
              </Button>
            </div>

            {/* Calculated Totals Display */}
            {calculatedTotals && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Rooms Total ({calculatedTotals.totalGuests} guests):</span>
                  <span className="font-medium text-slate-900">£{calculatedTotals.roomsTotal.toFixed(2)}</span>
                </div>
                {calculatedTotals.addOnsTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Add-ons Total:</span>
                    <span className="font-medium text-slate-900">£{calculatedTotals.addOnsTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-violet-200 pt-2">
                  <span className="text-slate-700 font-medium">{calculatedTotals.discountAmount > 0 ? 'Original Subtotal:' : 'Subtotal:'}</span>
                  <span className="font-bold text-slate-900">£{calculatedTotals.subtotal.toFixed(2)}</span>
                </div>
                {calculatedTotals.discountAmount > 0 && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-600">Discount{editBookingForm.discountType === 'percentage' ? ` (${parseFloat(editBookingForm.discountValue) || 0}%)` : ''}:</span>
                      <span className="font-medium text-emerald-600">−£{calculatedTotals.discountAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700 font-medium">Revised Total:</span>
                      <span className="font-bold text-slate-900" data-testid="edit-calc-revised-total">£{calculatedTotals.discountedSubtotal.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total Deposit:</span>
                  <span className="font-medium text-slate-900">£{calculatedTotals.totalDeposit.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Amount Paid:</span>
                  <span className="font-medium text-green-600">£{calculatedTotals.amountPaid.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-violet-200 pt-2">
                  <span className="text-slate-700 font-medium">Balance Due:</span>
                  <span className={`font-bold ${calculatedTotals.balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    £{calculatedTotals.balance.toFixed(2)}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={applyCalculatedTotals}
                  className="w-full mt-2 text-violet-600 border-violet-300 hover:bg-violet-100"
                  data-testid="edit-booking-apply-totals"
                >
                  Apply as Overrides
                </Button>
              </div>
            )}

            {/* Price Override Fields */}
            <p className="text-xs text-slate-500">Override calculated amounts if needed:</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-700">Deposit Override (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Auto-calculated"
                  value={editBookingForm.deposit_override || ''}
                  onChange={(e) => setEditBookingForm(prev => ({ ...prev, deposit_override: e.target.value }))}
                  className="bg-white border-slate-300"
                  data-testid="edit-booking-deposit-override"
                />
              </div>
              <div>
                <Label className="text-slate-700">Total Override (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Auto-calculated"
                  value={editBookingForm.total_override || ''}
                  onChange={(e) => setEditBookingForm(prev => ({ ...prev, total_override: e.target.value }))}
                  className="bg-white border-slate-300"
                  data-testid="edit-booking-total-override"
                />
              </div>
            </div>
          </div>

          {/* Discount */}
          <div className="space-y-3 border border-violet-200 bg-violet-50/40 rounded-lg p-4" data-testid="edit-discount-section">
            <h3 className="font-medium text-sm text-slate-700">Discount</h3>
            <p className="text-xs text-slate-500">
              Applies to the accommodation subtotal only. Blocked on bookings with an active/paused
              payment plan or where it would drop the total below the amount already paid.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-700">Discount Type</Label>
                <Select
                  value={editBookingForm.discountType}
                  onValueChange={(val) => setEditBookingForm(prev => ({ ...prev, discountType: val }))}
                >
                  <SelectTrigger className="bg-white border-slate-300" data-testid="edit-discount-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No discount</SelectItem>
                    <SelectItem value="fixed">Fixed amount (£)</SelectItem>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editBookingForm.discountType !== 'none' && (
                <div>
                  <Label className="text-slate-700">
                    {editBookingForm.discountType === 'percentage' ? 'Value (%)' : 'Value (£)'}
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editBookingForm.discountValue}
                    onChange={(e) => setEditBookingForm(prev => ({ ...prev, discountValue: e.target.value }))}
                    placeholder={editBookingForm.discountType === 'percentage' ? 'e.g. 10' : 'e.g. 50'}
                    className="bg-white border-slate-300"
                    data-testid="edit-discount-value"
                  />
                </div>
              )}
            </div>
            {editBookingForm.discountType !== 'none' && (
              <div>
                <Label className="text-slate-700">Reason *</Label>
                <Input
                  value={editBookingForm.discountReason}
                  onChange={(e) => setEditBookingForm(prev => ({ ...prev, discountReason: e.target.value }))}
                  placeholder="Reason for this discount (required)"
                  className="bg-white border-slate-300"
                  data-testid="edit-discount-reason"
                />
              </div>
            )}
            {booking.discount?.amount > 0 && (
              <p className="text-xs text-emerald-700" data-testid="edit-current-discount">
                Current discount: −£{Number(booking.discount.amount).toFixed(2)}
                {booking.discount.type === 'percentage' ? ` (${booking.discount.value}%)` : ''}
                {booking.discount.reason ? ` — ${booking.discount.reason}` : ''}
              </p>
            )}
          </div>

          {/* Guest Names & Emails */}
          {editBookingForm.guests.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-slate-700">Guest Names & Emails ({editBookingForm.guests.length})</h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {editBookingForm.guests.map((guest, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-500" />
                      <Input
                        value={guest.first_name || ''}
                        onChange={(e) => updateEditGuestName(idx, 'first_name', e.target.value)}
                        placeholder="First name"
                        className="flex-1 bg-white border-slate-300"
                        data-testid={`edit-guest-${idx}-first-name`}
                      />
                      <Input
                        value={guest.last_name || ''}
                        onChange={(e) => updateEditGuestName(idx, 'last_name', e.target.value)}
                        placeholder="Last name"
                        className="flex-1 bg-white border-slate-300"
                        data-testid={`edit-guest-${idx}-last-name`}
                      />
                    </div>
                    <div className="flex items-center gap-2 pl-6">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <Input
                        type="email"
                        value={guest.email || ''}
                        onChange={(e) => updateEditGuestName(idx, 'email', e.target.value)}
                        placeholder="Email (optional - for CC on booking emails)"
                        className="flex-1 bg-white border-slate-300 text-sm"
                        data-testid={`edit-guest-${idx}-email`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">Guests with emails will be CC'd on confirmation and payment emails</p>
            </div>
          )}

          {/* Add-ons */}
          {addOns.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-slate-700">Add-ons</h3>
              <p className="text-xs text-slate-500">Select how many passengers need each extra. Totals update automatically.</p>
              <div className="space-y-2">
                {addOns.map((addOn, idx) => {
                  const roomOpts = loadedEvent?.room_options || loadedEvent?.room_types || [];
                  const maxGuests = Object.entries(editBookingForm.rooms || {}).reduce((sum, [type, q]) => {
                    const rd = roomOpts.find(r => r.type.toLowerCase() === type.toLowerCase());
                    return sum + (rd ? (rd.capacity || 2) * q : 0);
                  }, 0);
                  const qty = editBookingForm.addOnQuantities[addOn.name] || 0;
                  const lineTotal = (addOn.price || 0) * qty;
                  return (
                  <div key={idx} className="p-2 bg-slate-50 rounded" data-testid={`edit-addon-row-${addOn.name}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <span className="text-sm text-slate-800">{addOn.name}</span>
                        <span className="text-violet-600 text-sm ml-1">£{addOn.price} pp</span>
                        {addOn.deposit_amount > 0 && (
                          <span className="text-xs text-slate-500 block">(£{addOn.deposit_amount} deposit pp)</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setEditAddOnQuantity(addOn.name, qty - 1)}
                          disabled={qty === 0}
                          data-testid={`edit-addon-minus-${addOn.name}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center text-sm font-medium" data-testid={`edit-addon-qty-${addOn.name}`}>{qty}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setEditAddOnQuantity(addOn.name, qty + 1)}
                          disabled={qty >= maxGuests}
                          data-testid={`edit-addon-plus-${addOn.name}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {qty > 0 && (
                      <p className="text-xs text-slate-500 mt-1">{qty} passenger{qty !== 1 ? 's' : ''} × £{addOn.price} = £{lineTotal.toFixed(2)}</p>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Special Requests */}
          <div>
            <Label className="text-slate-700">Special Requests</Label>
            <Textarea
              value={editBookingForm.special_requests}
              onChange={(e) => setEditBookingForm(prev => ({ ...prev, special_requests: e.target.value }))}
              rows={2}
              className="bg-white border-slate-300"
              data-testid="edit-booking-special-requests"
            />
          </div>

          {/* Status */}
          <div>
            <Label className="text-slate-700">Booking Status</Label>
            <Select
              value={editBookingForm.status}
              onValueChange={(val) => setEditBookingForm(prev => ({ ...prev, status: val }))}
            >
              <SelectTrigger className="bg-white border-slate-300" data-testid="edit-booking-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                <SelectItem value="fully_paid">Fully Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Amount Paid - Editable for corrections */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <Label className="text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Amount Paid (£) - Manual Correction
            </Label>
            <p className="text-xs text-amber-600 mb-2">
              Use this to correct incorrect payment totals. This will update the booking's payment balance.
            </p>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={editBookingForm.amount_paid}
              onChange={(e) => setEditBookingForm(prev => ({ ...prev, amount_paid: e.target.value }))}
              className="bg-white border-amber-300"
              data-testid="edit-booking-amount-paid"
            />
          </div>

          {/* Booking Summary */}
          <div className="bg-slate-50 rounded-lg p-4 text-sm">
            <h4 className="font-medium mb-2 text-slate-900">Booking Summary</h4>
            <div className="grid grid-cols-2 gap-1">
              <span className="text-slate-500">Reference:</span>
              <span className="text-slate-900">{booking.booking_id}</span>
              <span className="text-slate-500">Total:</span>
              <span className="text-slate-900">£{booking.total_amount?.toFixed(2)}</span>
              {booking.discount?.amount > 0 && (
                <>
                  <span className="text-slate-500">Discount:</span>
                  <span className="text-emerald-600">−£{Number(booking.discount.amount).toFixed(2)}</span>
                </>
              )}
              <span className="text-slate-500">Paid:</span>
              <span className="text-slate-900">£{booking.amount_paid?.toFixed(2)}</span>
              <span className="text-slate-500">Balance:</span>
              <span className={booking.total_amount > booking.amount_paid ? 'text-orange-600 font-medium' : 'text-green-600'}>
                £{Math.max(0, booking.total_amount - booking.amount_paid).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Payment Shares Section */}
          <div className="border-t pt-4">
            <DancerPaymentShareManager
              booking={booking}
              authToken={authToken}
              onUpdate={onSuccess}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-booking-cancel">
            Cancel
          </Button>
          <Button onClick={handleUpdateBooking} disabled={saving} data-testid="edit-booking-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
